import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Phone, Mail, MapPin, Search, ArrowUpRight, Car, FileText, Receipt } from "lucide-react";
import { CRM_API, errMsg } from "./api";
import PageHeader from "./components/PageHeader";

export function CustomersList() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      CRM_API.get(`/customers${q ? `?q=${encodeURIComponent(q)}` : ""}`)
        .then(({ data }) => { if (!cancelled) setRows(data); })
        .finally(() => !cancelled && setLoading(false));
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  return (
    <div data-testid="crm-customers-page">
      <PageHeader
        eyebrow="Directory"
        title="Customers"
        subtitle="Every driver who's ever brought a vehicle through the studio."
        right={
          <Link to="/crm/customers/new" data-testid="btn-new-customer"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white font-medium transition-colors">
            <UserPlus className="w-4 h-4" /> New customer
          </Link>
        }
      />
      <div className="px-6 md:px-10 py-6">
        <div className="flex items-center gap-2 mb-6 max-w-md">
          <div className="flex-1 flex items-center gap-2 px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg">
            <Search className="w-4 h-4 text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, phone, email…"
              className="flex-1 bg-transparent outline-none text-sm text-white placeholder-white/30"
              data-testid="customers-search" />
          </div>
        </div>
        {loading && <div className="text-white/40 text-sm">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-12 text-center border border-dashed border-white/10 rounded-xl">
            <div className="text-white/40 text-sm mb-3">No customers yet.</div>
            <Link to="/crm/customers/new" className="text-[#1F5EFF] hover:underline">Add your first customer →</Link>
          </div>
        )}
        {!loading && rows.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {rows.map((c) => (
              <Link key={c.id} to={`/crm/customers/${c.id}`} data-testid={`customer-card-${c.id}`}
                className="group p-5 rounded-xl bg-white/5 border border-white/10 hover:border-[#1F5EFF]/50 hover:bg-white/8 transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#1F5EFF]/30 to-[#1F5EFF]/10 border border-[#1F5EFF]/20 flex items-center justify-center text-[#8db2ff] font-medium">
                    {(c.first_name || "?").slice(0, 1)}{(c.last_name || "").slice(0, 1)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-medium truncate">{c.first_name} {c.last_name}</div>
                    <div className="font-mono-alt text-[10px] text-white/40 uppercase">
                      {c.vehicles_count} vehicle{c.vehicles_count === 1 ? "" : "s"}
                    </div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-white/30 group-hover:text-white transition-colors" />
                </div>
                <div className="space-y-1.5 text-sm text-white/60">
                  <div className="flex items-center gap-2 truncate"><Phone className="w-3.5 h-3.5 text-white/30 shrink-0" />{c.phone}</div>
                  {c.email && <div className="flex items-center gap-2 truncate"><Mail className="w-3.5 h-3.5 text-white/30 shrink-0" />{c.email}</div>}
                  {c.address && <div className="flex items-center gap-2 truncate"><MapPin className="w-3.5 h-3.5 text-white/30 shrink-0" />{c.address}</div>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function CustomerNew() {
  const nav = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", address: "", notes: "", marketing_consent: false });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, email: form.email.trim() || null, address: form.address.trim() || null, notes: form.notes.trim() || null };
      const { data } = await CRM_API.post("/customers", payload);
      toast.success("Customer created");
      if (data.duplicate_phone) toast.warning("Another customer has this phone number");
      nav(`/crm/customers/${data.id}`, { replace: true });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="crm-customer-new-page">
      <PageHeader eyebrow="Directory / new" title="New customer" subtitle="Two required fields — the rest can be filled later." />
      <form onSubmit={submit} className="px-6 md:px-10 py-8 max-w-3xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="First name *"><input required value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="input-first-name" className={inp} autoFocus /></Field>
          <Field label="Last name"><input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="input-last-name" className={inp} /></Field>
          <Field label="Phone *"><input required type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="input-phone" className={inp} inputMode="tel" /></Field>
          <Field label="Email"><input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="input-email" className={inp} /></Field>
          <Field label="Address" full><input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="input-address" className={inp} /></Field>
          <Field label="Notes" full><textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="input-notes" rows={3} className={inp + " resize-y"} /></Field>
        </div>
        <label className="mt-5 flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={form.marketing_consent} onChange={(e) => set("marketing_consent", e.target.checked)} data-testid="input-marketing" className="w-4 h-4 accent-[#1F5EFF]" />
          <span className="text-sm text-white/70">Customer consents to marketing communications</span>
        </label>
        <div className="mt-8 flex items-center gap-3">
          <button type="submit" disabled={busy} data-testid="btn-save-customer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white font-medium transition-colors disabled:opacity-50">
            {busy ? "Saving…" : "Save customer"}
          </button>
          <button type="button" onClick={() => nav(-1)} className="text-white/50 hover:text-white text-sm">Cancel</button>
        </div>
      </form>
    </div>
  );
}

export function CustomerDetail() {
  const [data, setData] = useState(null);
  const [comms, setComms] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [err, setErr] = useState(null);
  const cid = window.location.pathname.split("/").pop();
  const nav = useNavigate();

  useEffect(() => {
    CRM_API.get(`/customers/${cid}`).then(({ data }) => setData(data)).catch((e) => setErr(errMsg(e)));
    CRM_API.get(`/customers/${cid}/communications`).then(({ data }) => setComms(Array.isArray(data) ? data : [])).catch(() => {});
    CRM_API.get(`/customers/${cid}/timeline`).then(({ data }) => setTimeline(Array.isArray(data) ? data : [])).catch(() => {});
  }, [cid]);

  if (err) return <div className="p-10 text-red-300">{err}</div>;
  if (!data) return <div className="p-10 text-white/40">Loading…</div>;
  const c = data.customer;

  return (
    <div data-testid="crm-customer-detail">
      <PageHeader
        eyebrow="Customer"
        title={`${c.first_name} ${c.last_name || ""}`.trim()}
        subtitle={c.phone}
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/crm/estimates?new=1&customer=${c.id}`} data-testid="btn-new-estimate-for-customer"
              className="inline-flex items-center gap-2 px-4 py-3 rounded-lg bg-white/8 hover:bg-white/15 text-white text-sm font-medium transition-colors">
              <FileText className="w-4 h-4" /> Estimate
            </Link>
            <Link to={`/crm/invoices?new=1&customer=${c.id}`} data-testid="btn-new-invoice-for-customer"
              className="inline-flex items-center gap-2 px-4 py-3 rounded-lg bg-white/8 hover:bg-white/15 text-white text-sm font-medium transition-colors">
              <Receipt className="w-4 h-4" /> Invoice
            </Link>
            <Link to={`/crm/vehicles/new?customer=${c.id}`} data-testid="btn-add-vehicle"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#1F5EFF] hover:bg-white hover:text-black text-white font-medium transition-colors">
              <Car className="w-4 h-4" /> Add vehicle
            </Link>
          </div>
        }
      />
      <div className="px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <aside className="lg:col-span-1 space-y-2 text-sm">
          <InfoRow label="Phone" value={c.phone} />
          <InfoRow label="Email" value={c.email || "—"} />
          <InfoRow label="Address" value={c.address || "—"} />
          <InfoRow label="Marketing" value={c.marketing_consent ? "Consented" : "Opted-out"} />
          <InfoRow label="Lifetime spend" value={`$${(c.lifetime_spend || 0).toFixed(2)}`} />
          <InfoRow label="Added" value={new Date(c.created_at).toLocaleDateString()} />
        </aside>
        <section className="lg:col-span-2 space-y-4">
          <PanelHead title={`Vehicles (${data.vehicles.length})`} />
          {data.vehicles.length === 0 ? (
            <div className="p-8 text-center border border-dashed border-white/10 rounded-xl text-white/40 text-sm">
              No vehicles yet — <Link to={`/crm/vehicles/new?customer=${c.id}`} className="text-[#1F5EFF] hover:underline">add one</Link>.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.vehicles.map((v) => (
                <button key={v.id} onClick={() => nav(`/crm/vehicles/${v.id}`)} data-testid={`vehicle-${v.id}`}
                  className="text-left p-4 rounded-xl bg-white/5 border border-white/10 hover:border-[#1F5EFF]/40 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <Car className="w-4 h-4 text-[#1F5EFF]" />
                    <div className="text-white text-sm font-medium truncate">
                      {[v.year, v.make, v.model].filter(Boolean).join(" ") || "Unnamed"}
                    </div>
                  </div>
                  <div className="font-mono-alt text-[10px] text-white/40">{[v.vin, v.license_plate].filter(Boolean).join(" · ") || "—"}</div>
                </button>
              ))}
            </div>
          )}
          <PanelHead title={`Communications (${comms.length})`} />
          {comms.length === 0 ? (
            <div className="p-6 text-center border border-dashed border-white/10 rounded-xl text-white/40 text-sm">
              No emails yet — auto-acks, your replies from the Dashboard, and estimate/invoice sends appear here.
            </div>
          ) : (
            <div className="space-y-2" data-testid="customer-communications">
              {comms.map((m, i) => (
                <div key={i} data-testid={`comm-entry-${i}`} className="p-3.5 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Mail className="w-3.5 h-3.5 text-[#1F5EFF] shrink-0" />
                    <span className="text-white text-sm truncate flex-1 min-w-0">{m.subject || m.body}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider ${m.ok ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>
                      {m.ok ? "SENT" : "FAILED"}
                    </span>
                    <span className="text-white/40 font-mono-alt text-[10px] whitespace-nowrap">{(m.at || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                  {m.subject && m.body && <div className="text-white/50 text-xs line-clamp-2">{m.body}</div>}
                  <div className="text-white/30 text-[10px] font-mono-alt mt-1">
                    {m.kind === "auto_ack" ? "auto-acknowledgement" : (m.kind || m.source)}{m.ref ? ` · ${m.ref}` : ""} · to {m.to || "—"} · by {m.by || "—"}
                  </div>
                </div>
              ))}
            </div>
          )}
          <PanelHead title={`Service history (${data.appointments.length})`} />
          {data.appointments.length === 0 ? (
            <div className="p-6 text-center border border-dashed border-white/10 rounded-xl text-white/40 text-sm">
              No visits yet — completed appointments appear here with services, dates and amounts.
            </div>
          ) : (
            <div className="space-y-2" data-testid="customer-service-history">
              {data.appointments.map((a) => {
                const veh = data.vehicles.find((v) => v.id === a.vehicle_id);
                const ceramic = (a.services || []).filter((s) => s.group === "Ceramic Coating");
                return (
                  <div key={a.id} data-testid={`history-${a.id}`}
                    className="p-3.5 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="text-white text-sm font-medium min-w-0 truncate">{a.service || "Service"}</div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-mono-alt uppercase tracking-widest border ${
                          a.status === "completed" ? "bg-emerald-400/10 text-emerald-300 border-emerald-400/40"
                            : "bg-white/5 text-white/50 border-white/15"}`}>
                          {a.status}
                        </span>
                        {a.amount != null && (
                          <span className="text-white/70 text-sm tabular-nums">${Number(a.amount).toFixed(2)}</span>
                        )}
                      </div>
                    </div>
                    <div className="mt-1 text-white/40 text-xs font-mono-alt">
                      {(a.date || "").slice(0, 10)}{veh ? ` · ${[veh.year, veh.make, veh.model].filter(Boolean).join(" ")}` : ""} · ref {a.reference?.slice(0, 8)}
                    </div>
                    {ceramic.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {ceramic.map((s) => (
                          <span key={s.key} className="px-2 py-0.5 rounded-full bg-[#1F5EFF]/10 border border-[#1F5EFF]/30 text-[#8db2ff] text-[10px]">
                            {s.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {(a.bundles || []).length > 0 && (
                      <div className="mt-1 text-[11px] text-white/40">
                        Package: {a.bundles.map((b) => b.name).join(" + ")}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <PanelHead title="Timeline" />
          {timeline.length === 0 ? (
            <div className="p-6 text-center border border-dashed border-white/10 rounded-xl text-white/40 text-sm">
              No events yet.
            </div>
          ) : (
            <div className="relative pl-5 space-y-3 before:absolute before:left-1.5 before:top-1 before:bottom-1 before:w-px before:bg-white/10" data-testid="customer-timeline">
              {timeline.map((t, i) => (
                <div key={i} className="relative" data-testid={`timeline-entry-${i}`}>
                  <span className={`absolute -left-[15px] top-1.5 w-2.5 h-2.5 rounded-full border-2 border-[#08080b] ${
                    t.kind === "service" ? "bg-[#1F5EFF]" : t.kind === "followup" ? "bg-amber-400" : "bg-white/40"}`} />
                  <div className="text-white/40 font-mono-alt text-[10px]">{(t.at || "").slice(0, 16).replace("T", " ")}</div>
                  <div className="text-white/80 text-sm">{t.title}{t.amount != null ? ` — $${Number(t.amount).toFixed(2)}` : ""}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

/* ---- shared ---- */
const inp = "w-full bg-white/5 border border-white/12 rounded-lg px-4 py-3 outline-none focus:border-[#1F5EFF] transition-colors text-white text-[15px]";
const Field = ({ label, children, full }) => (
  <label className={`block ${full ? "md:col-span-2" : ""}`}>
    <div className="text-[11px] font-mono-alt text-white/50 mb-1.5 tracking-widest">{label}</div>
    {children}
  </label>
);
const InfoRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 p-3 rounded-lg bg-white/5 border border-white/8">
    <div className="text-[11px] font-mono-alt text-white/40 uppercase tracking-widest">{label}</div>
    <div className="text-white text-sm text-right break-all">{value}</div>
  </div>
);
const PanelHead = ({ title }) => (
  <div className="flex items-center gap-3">
    <span className="w-6 h-px bg-[#1F5EFF]" />
    <span className="font-mono-alt text-[10px] tracking-widest text-white/50 uppercase">{title}</span>
  </div>
);
